import React from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import BackendAxios from "../BackendAxios";

function moveItem(array, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return array;
  }

  const nextArray = [...array];
  const [movedItem] = nextArray.splice(fromIndex, 1);
  nextArray.splice(toIndex, 0, movedItem);
  return nextArray;
}

class FieldsManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fields: [],
      loading: false,
      error: null,
      dialogOpen: false,
      editingField: null,
      fieldName: "",
      fieldType: "text",
      fieldShowOnGallery: false,
      deleteDialogOpen: false,
      fieldToDelete: null,
      successMessage: null,
      draggedFieldId: null,
      orderConfirmOpen: false,
    };
  }

  componentDidMount() {
    this.fetchFields();
  }

  fetchFields = async () => {
    this.setState({ loading: true, error: null });
    try {
      const response = await BackendAxios.get("/fields");
      this.setState({ fields: response.data, loading: false });
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to fetch fields",
        loading: false,
      });
    }
  };

  handleOpenDialog = (field = null) => {
    if (field) {
      this.setState({
        editingField: field,
        fieldName: field.name,
        fieldType: field.type || "text",
        fieldShowOnGallery: field.showOnGallery || false,
        dialogOpen: true,
      });
    } else {
      this.setState({
        editingField: null,
        fieldName: "",
        fieldType: "text",
        fieldShowOnGallery: false,
        dialogOpen: true,
      });
    }
    this.setState({ error: null, successMessage: null });
  };

  handleCloseDialog = () => {
    this.setState({
      dialogOpen: false,
      editingField: null,
      fieldName: "",
      fieldType: "text",
      fieldShowOnGallery: false,
      error: null,
      successMessage: null,
    });
  };

  handleNameChange = (event) => {
    this.setState({ fieldName: event.target.value });
  };

  handleTypeChange = (event) => {
    this.setState({ fieldType: event.target.value });
  };

  handleShowOnGalleryChange = (event) => {
    this.setState({ fieldShowOnGallery: event.target.checked });
  };

  handleSave = async () => {
    const { fieldName, fieldType, fieldShowOnGallery, editingField } = this.state;

    if (!fieldName || !fieldName.trim()) {
      this.setState({ error: "Field name is required" });
      return;
    }

    this.setState({ loading: true, error: null, successMessage: null });

    try {
      if (editingField) {
        await BackendAxios.put(`/fields/${editingField._id}`, {
          name: fieldName.trim(),
          type: fieldType,
          showOnGallery: fieldShowOnGallery,
        });
        this.setState({ successMessage: "Field updated successfully" });
      } else {
        await BackendAxios.post("/fields", {
          name: fieldName.trim(),
          type: fieldType,
          showOnGallery: fieldShowOnGallery,
        });
        this.setState({ successMessage: "Field created successfully" });
      }

      setTimeout(() => {
        this.fetchFields();
        this.handleCloseDialog();
      }, 1000);
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to save field",
        loading: false,
      });
    }
  };

  handleDeleteClick = (field) => {
    this.setState({ fieldToDelete: field, deleteDialogOpen: true });
  };

  handleDeleteCancel = () => {
    this.setState({ deleteDialogOpen: false, fieldToDelete: null });
  };

  handleDeleteConfirm = async () => {
    const { fieldToDelete } = this.state;
    if (!fieldToDelete) return;

    this.setState({ loading: true, error: null, deleteDialogOpen: false });
    try {
      await BackendAxios.delete(`/fields/${fieldToDelete._id}`);
      this.fetchFields();
      this.setState({ fieldToDelete: null, successMessage: "Field deleted successfully" });
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to delete field",
        loading: false,
        fieldToDelete: null,
      });
    }
  };

  handleFieldDragStart = (fieldId) => {
    this.setState({ draggedFieldId: fieldId });
  };

  handleFieldDragEnd = () => {
    this.setState({ draggedFieldId: null });
  };

  handleFieldDrop = (targetFieldId) => {
    this.setState((prevState) => {
      const fromIndex = prevState.fields.findIndex(
        (field) => field._id === prevState.draggedFieldId
      );
      const toIndex = prevState.fields.findIndex(
        (field) => field._id === targetFieldId
      );

      return {
        fields: moveItem(prevState.fields, fromIndex, toIndex),
        draggedFieldId: null,
      };
    });
  };

  handleSaveFieldOrderClick = () => {
    this.setState({ orderConfirmOpen: true, error: null, successMessage: null });
  };

  handleSaveFieldOrderCancel = () => {
    this.setState({ orderConfirmOpen: false });
  };

  handleSaveFieldOrderConfirm = async () => {
    this.setState({ loading: true, error: null, successMessage: null });

    try {
      const fieldOrder = this.state.fields.map((field, index) => ({
        id: field._id,
        displayOrder: index,
      }));

      const response = await BackendAxios.put("/fields/order", {
        fieldOrder,
      });

      this.setState({
        fields: response.data.fields || this.state.fields,
        loading: false,
        orderConfirmOpen: false,
        successMessage: "Field order updated successfully",
      });
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to update field order",
        loading: false,
        orderConfirmOpen: false,
      });
    }
  };

  render() {
    const {
      fields,
      loading,
      error,
      dialogOpen,
      fieldName,
      fieldType,
      fieldShowOnGallery,
      editingField,
      successMessage,
      deleteDialogOpen,
      fieldToDelete,
      draggedFieldId,
      orderConfirmOpen,
    } = this.state;

    return (
      <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h5">Fields Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Drag fields to control the order they appear on browse designs.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.handleOpenDialog()}
            sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
          >
            Create Field
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => this.setState({ successMessage: null })}
          >
            {successMessage}
          </Alert>
        )}

        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            boxSizing: "border-box",
          }}
        >
          <Table sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 72 }}>Order</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Show on Gallery</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && fields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : fields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No fields found
                  </TableCell>
                </TableRow>
              ) : (
                fields.map((field, index) => (
                  <TableRow
                    key={field._id}
                    draggable
                    onDragStart={() => this.handleFieldDragStart(field._id)}
                    onDragEnd={this.handleFieldDragEnd}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => this.handleFieldDrop(field._id)}
                    sx={{
                      cursor: fields.length > 1 ? "grab" : "default",
                      backgroundColor:
                        draggedFieldId === field._id
                          ? "rgba(91, 113, 149, 0.12)"
                          : "transparent",
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DragIndicatorIcon sx={{ color: "#7b8798", fontSize: 18 }} />
                        <Typography variant="body2" color="text.secondary">
                          {index + 1}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{field.name}</TableCell>
                    <TableCell>{field.type || "text"}</TableCell>
                    <TableCell>{field.showOnGallery ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      {new Date(field.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => this.handleOpenDialog(field)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => this.handleDeleteClick(field)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Button
            variant="outlined"
            onClick={this.handleSaveFieldOrderClick}
            disabled={loading || fields.length < 2}
          >
            Save Field Order
          </Button>
        </Box>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={this.handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingField ? "Edit Field" : "Create Field"}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
              <TextField
                label="Field Name"
                value={fieldName}
                onChange={this.handleNameChange}
                fullWidth
                required
              />

              <FormControl fullWidth>
                <InputLabel>Field Type</InputLabel>
                <Select value={fieldType} onChange={this.handleTypeChange} label="Field Type">
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="squareFootage">Square Footage</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="boolean">Boolean</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={fieldShowOnGallery}
                    onChange={this.handleShowOnGalleryChange}
                  />
                }
                label="Show on Gallery"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseDialog}>Cancel</Button>
            <Button
              onClick={this.handleSave}
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
            >
              {loading ? <CircularProgress size={24} /> : editingField ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={this.handleDeleteCancel}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the field "
              <strong>{fieldToDelete?.name}</strong>"?
            </Typography>
            <Typography sx={{ mt: 1, color: "error.main" }}>
              This action cannot be undone. Entries using this field will lose this field value.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleDeleteCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={this.handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={orderConfirmOpen} onClose={this.handleSaveFieldOrderCancel}>
          <DialogTitle>Save Field Order</DialogTitle>
          <DialogContent>
            <Typography>
              Save this field order for Browse Designs?
            </Typography>
            <Typography sx={{ mt: 1 }} color="text.secondary">
              Cards, detail panels, and browse filters will use this order.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleSaveFieldOrderCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={this.handleSaveFieldOrderConfirm}
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Save Order"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default FieldsManagement;

